/**
 * PDF Generation using jsPDF Direct API
 * Constructs PDF programmatically without HTML rendering
 * Provides reliable layout, pagination, and footer placement
 */
import jsPDF from 'jspdf';

// PDF Constants
const PDF_CONFIG = {
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    margin: { top: 15, left: 15, right: 15, bottom: 20 },
    pageHeight: 277, // A4 height 297mm - 20mm margins
    pageWidth: 180, // A4 width 210mm - 30mm margins
};

// Colors
const COLORS = {
    darkBlue: { r: 0, g: 31, b: 63 },      // #001f3f
    darkGray: { r: 51, g: 51, b: 51 },     // #333
    mediumGray: { r: 102, g: 102, b: 102 }, // #666
    lightGray: { r: 240, g: 240, b: 240 },  // #f0f0f0
    lightText: { r: 153, g: 153, b: 153 },  // #999
};

// Table Layout Configuration
const TABLE_CONFIG = {
    rowHeight: 6,
    headerHeight: 6,
    footerHeight: 20,
    cellPadding: 1,
    rightMargin: 30,
    footerOffset: 5,
};

/**
 * Main function: Generate invoice PDF using jsPDF direct API
 * @param {Object} invoiceData - Invoice record
 * @param {Array} serviceCharges - Service charge items
 * @param {Object} summary - Summary with subtotal, totalAdditionalCharges, totalDue
 * @param {Object} additionalData - Additional data (displayCompany, displaySeller, etc.)
 */
/**
 * Legacy wrapper for DOM-based PDF generation
 * Note: This is deprecated. Use generateInvoicePDFDirect() with invoice data instead.
 * Kept for backward compatibility with legacy components.
 * 
 * @param {HTMLElement} element - Invoice HTML element to convert
 * @param {string} invoiceNumber - Invoice number for filename
 * @returns {void}
 * @deprecated Use generateInvoicePDFDirect instead
 */
export const generateInvoicePDF = async (element, invoiceNumber) => {
    console.warn(
        '⚠️ generateInvoicePDF() is deprecated. Please use generateInvoicePDFDirect() instead with invoice data.',
        'Legacy DOM-based approach has reliability issues.'
    );
    
    if (!element) {
        console.error('Invoice element not found');
        alert('Unable to generate PDF. Invoice content not found.');
        return;
    }
    
    try {
        if (!globalThis?.html2pdf) {
            console.error('html2pdf library not available');
            alert('PDF library not available. Please refresh the page.');
            return;
        }
        
        const pdf = globalThis.html2pdf();
        pdf.set({
            margin: [10, 10, 10, 10],
            filename: `Invoice-${invoiceNumber}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
            },
            jsPDF: {
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            },
            pagebreak: {
                mode: ['css'],
                avoid: ['.invoice-branding-section', '.bill-from-to-section', '.period-cards-section', '.invoice-summary-section'],
            },
        })
            .from(element)
            .save()
            .then(() => {
                console.log('✓ PDF generated (legacy):', `Invoice-${invoiceNumber}.pdf`);
            })
            .catch(error => {
                console.error('✗ Error saving PDF:', error);
                alert('Failed to download invoice PDF. Please try again.');
            });
    } catch (error) {
        console.error('✗ Error generating PDF:', error);
        alert('Failed to generate invoice PDF. Please try again.');
    }
};

export const generateInvoicePDFDirect = async (invoiceData, serviceCharges, summary, additionalData = {}) => {
    try {
        if (!invoiceData?.invoiceNumber) {
            console.error('Invalid invoice data');
            alert('Unable to generate PDF. Invoice data is missing.');
            return;
        }

        console.log('Generating PDF (jsPDF Direct) for invoice:', invoiceData.invoiceNumber);

        const {
            displayCompany = invoiceData.companyName || 'N/A',
            displaySeller = invoiceData.sellerName || '',
            periodFromDate = 'N/A',
            periodToDate = 'N/A',
            hasRentItems = false,
        } = additionalData;

        const warehouseName = invoiceData.warehouseName || 'N/A';
        const sellerCode = invoiceData.sellerCode || 'N/A';

        // Initialize PDF document
        const doc = new jsPDF({
            orientation: PDF_CONFIG.orientation,
            unit: PDF_CONFIG.unit,
            format: PDF_CONFIG.format,
        });

        let yPosition = PDF_CONFIG.margin.top;
        const pageHeight = PDF_CONFIG.pageHeight;
        const margin = PDF_CONFIG.margin;

        // ===== SECTION 1: Invoice Header =====
        yPosition = addInvoiceHeader(doc, invoiceData, displayCompany, displaySeller, yPosition);
        yPosition += 5; // Spacing

        // ===== SECTION 2: Bill From / To =====
        yPosition = addBillFromTo(
            doc,
            invoiceData,
            displayCompany,
            displaySeller,
            sellerCode,
            warehouseName,
            yPosition
        );
        yPosition += 5;

        // ===== SECTION 3: Period & Cycle =====
        yPosition = addPeriodAndCycle(doc, periodFromDate, periodToDate, hasRentItems, yPosition);
        yPosition += 8;

        // ===== SECTION 4: Service Charges Table =====
        yPosition = addServiceChargesTable(doc, serviceCharges, yPosition, pageHeight, margin);
        yPosition += 5;

        // ===== SECTION 5: Summary =====
        addSummary(doc, summary, yPosition, pageHeight, margin);

        // ===== SECTION 6: Footer =====
        addFooter(doc, pageHeight);

        // Save PDF
        doc.save(`Invoice-${invoiceData.invoiceNumber}.pdf`);
        console.log('✓ PDF generated successfully (jsPDF Direct):', `Invoice-${invoiceData.invoiceNumber}.pdf`);
    } catch (error) {
        console.error('✗ Error generating PDF (jsPDF Direct):', error);
        alert('Failed to generate invoice PDF. Please try again.');
    }
};

/**
 * Helper: Add invoice header section
 */
const addInvoiceHeader = (doc, invoiceData, company, seller, yPosition) => {
    setFontStyle(doc, 18, 'bold', COLORS.darkBlue);
    doc.text(`Invoice ${invoiceData.invoiceNumber}`, PDF_CONFIG.margin.left, yPosition);
    yPosition += 8;

    setFontStyle(doc, 13, 'normal', COLORS.mediumGray);
    const companyText = seller ? `${company} — ${seller}` : company;
    doc.text(companyText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 6;

    setFontStyle(doc, 11, 'normal', COLORS.lightText);
    const issuedText = `Invoice Date: ${invoiceData.invoiceDate || 'N/A'}`;
    doc.text(issuedText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 6;

    return yPosition;
};

/**
 * Helper: Add Bill From / To section
 */
const addBillFromTo = (doc, invoiceData, company, seller, sellerCode, warehouseName, yPosition) => {
    const colWidth = (PDF_CONFIG.pageWidth - 20) / 2; // Two columns
    const leftX = PDF_CONFIG.margin.left;
    const rightX = PDF_CONFIG.margin.left + colWidth + 10;
    const startY = yPosition;

    // BILL FROM (Left Column)
    setFontStyle(doc, 12, 'bold', COLORS.darkGray);
    doc.text('BILL FROM', leftX, yPosition);
    yPosition += 5;

    setFontStyle(doc, 11, 'normal', COLORS.mediumGray);
    doc.text('3PLVision Warehouse Operations', leftX, yPosition);
    yPosition += 4;
    doc.text(`Warehouse: ${warehouseName}`, leftX, yPosition);
    yPosition += 4;
    doc.text('info@3plvision.com', leftX, yPosition);
    yPosition += 4;
    doc.text('WhatsApp: +1 732-277-2073', leftX, yPosition);

    // BILL TO (Right Column)
    yPosition = startY;
    setFontStyle(doc, 12, 'bold', COLORS.darkGray);
    doc.text('BILL TO', rightX, yPosition);
    yPosition += 5;

    setFontStyle(doc, 11, 'normal', COLORS.mediumGray);
    doc.text(company, rightX, yPosition);
    yPosition += 4;
    if (seller) {
        doc.text(`Seller: ${seller}`, rightX, yPosition);
        yPosition += 4;
    }
    doc.text(`Seller Code: ${sellerCode}`, rightX, yPosition);

    return startY + 20; // Fixed height for this section
};

/**
 * Helper: Add Period & Cycle section
 */
const addPeriodAndCycle = (doc, fromDate, toDate, hasRentItems, yPosition) => {
    setFontStyle(doc, 12, 'normal', COLORS.mediumGray);

    const periodText = `Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`;
    doc.text(periodText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 5;

    const cycleText = `Cycle: ${hasRentItems ? 'Monthly' : 'Daily'}`;
    doc.text(cycleText, PDF_CONFIG.margin.left, yPosition);
    yPosition += 5;

    return yPosition;
};

/**
 * Helper: Add service charges table (handles multi-page)
 */
const addServiceChargesTable = (doc, charges, yPosition, pageHeight, margin) => {
    const footerHeight = 20; // Space reserved for footer
    const cellPadding = 3;

    // Column widths (adjusted for better content fit)
    // Total: 12+32+36+20+12+10+14+14+16 = 166mm (14mm margin buffer)
    const cols = {
        id: 12,
        service: 32,
        ref: 36,
        txn: 20,
        unit: 12,
        qty: 10,
        rate: 14,
        add: 14,
        amount: 16,
    };
    const totalTableWidth = Object.values(cols).reduce((a, b) => a + b, 0);

    // Table header
    yPosition = addTableHeader(doc, yPosition, cols, margin);
    yPosition += 1; // Minimal spacing between header and first row

    // Table rows
    charges.forEach((charge, idx) => {
        const skuText = String(charge.sku || 'N/A');
        const trackingText = String(charge.ref || 'N/A');
        const wrapText = text =>
            typeof doc.splitTextToSize === 'function' ? doc.splitTextToSize(text, cols.ref - cellPadding * 2) : [text];
        const skuTrackingLines = [
            ...wrapText(skuText),
            ...wrapText(trackingText),
        ];
        const rowHeight = Math.max(TABLE_CONFIG.rowHeight, skuTrackingLines.length * 4 + 2);

        // Check if new page needed
        if (yPosition + rowHeight + 5 > pageHeight - footerHeight) {
            doc.addPage();
            yPosition = margin.top;
            yPosition = addTableHeader(doc, yPosition, cols, margin);
            yPosition += 1; // Minimal spacing between header and first row
        }

        // Alternate row colors
        const bgColor = idx % 2 === 0 ? [255, 255, 255] : [249, 249, 249];
        doc.setFillColor(...bgColor);
        doc.rect(
            margin.left,
            yPosition,
            totalTableWidth,
            rowHeight,
            'F'
        );

        // Row content (vertically centered within the row height)
        setFontStyle(doc, 9, 'normal', COLORS.mediumGray);
        const values = [
            String(idx + 1).substring(0, 8),
            String(charge.service || '').substring(0, 20),
            skuTrackingLines,
            String(charge.txn || '').substring(0, 12),
            String(charge.unit || '').substring(0, 8),
            String(charge.qty || ''),
            charge.rate === null ? '-' : `$${charge.rate.toFixed(2)}`,
            `$${(charge.add || 0).toFixed(2)}`,
            `$${(charge.amount || 0).toFixed(2)}`,
        ];
        const widths = [cols.id, cols.service, cols.ref, cols.txn, cols.unit, cols.qty, cols.rate, cols.add, cols.amount];
        const alignments = ['left', 'left', 'left', 'left', 'left', 'center', 'right', 'right', 'right'];
        const textBaselineY = yPosition + rowHeight / 2 + 1; // Vertically centered

        let xPos = margin.left;
        values.forEach((val, i) => {
            const align = alignments[i];
            const colWidth = widths[i];
            let textX = xPos + cellPadding; // Padding from left edge
            
            if (align === 'right') {
                textX = xPos + colWidth - cellPadding; // Padding from right edge
            } else if (align === 'center') {
                textX = xPos + colWidth / 2; // Centered within column
            }

            // SKU/tracking can wrap within its column; other compact fields stay single-line.
            doc.text(val, textX, Array.isArray(val) ? yPosition + 4 : textBaselineY, {
                align: align === 'center' ? 'center' : align,
            });
            xPos += colWidth;
        });

        yPosition += rowHeight;
    });

    return yPosition;
};

/**
 * Helper: Add table header row
 */
const addTableHeader = (doc, yPosition, cols, margin) => {
    const headers = ['#', 'SERVICE', 'SKU (Tracking #)', 'TXN', 'UNIT', 'QTY', 'RATE', 'ADD.', 'AMOUNT'];
    const widths = [cols.id, cols.service, cols.ref, cols.txn, cols.unit, cols.qty, cols.rate, cols.add, cols.amount];
    const alignments = ['left', 'left', 'left', 'left', 'left', 'center', 'right', 'right', 'right'];
    const cellPadding = 3;

    // Header background rectangle (positioned at correct Y)
    const totalWidth = Object.values(cols).reduce((a, b) => a + b, 0);
    doc.setFillColor(COLORS.darkBlue.r, COLORS.darkBlue.g, COLORS.darkBlue.b);
    doc.rect(margin.left, yPosition, totalWidth, TABLE_CONFIG.headerHeight, 'F');

    // Header text (vertically centered within the rectangle)
    setFontStyle(doc, 9, 'bold', { r: 255, g: 255, b: 255 }); // White text
    const textBaselineY = yPosition + TABLE_CONFIG.headerHeight / 2 + 1; // Center text in rectangle

    let xPos = margin.left;
    headers.forEach((header, i) => {
        const align = alignments[i];
        const colWidth = widths[i];
        let textX = xPos + cellPadding; // Padding from left edge
        
        if (align === 'right') {
            textX = xPos + colWidth - cellPadding; // Padding from right edge
        } else if (align === 'center') {
            textX = xPos + colWidth / 2; // Centered within column
        }

        doc.text(header, textX, textBaselineY, {
            align: align === 'center' ? 'center' : align,
        });
        xPos += colWidth;
    });

    // Return Y position just below the header (for first data row)
    return yPosition + TABLE_CONFIG.headerHeight;
};

/**
 * Helper: Add summary section
 */
const addSummary = (doc, summary, yPosition, pageHeight, margin) => {
    const footerReserved = TABLE_CONFIG.footerHeight;
    const summaryMinSpace = 35; // Minimum space needed for summary section

    // Check if sufficient space remains on current page; if not, go to new page
    if (yPosition + summaryMinSpace > pageHeight - footerReserved) {
        doc.addPage();
        yPosition = margin.top;
    }

    // Add top margin to separate summary from table
    yPosition += 8;

    // Position summary on the right side of the page
    const labelWidth = 60;
    const rightMargin = 15; // Margin from right edge of page
    const summaryX = margin.left + PDF_CONFIG.pageWidth - rightMargin - labelWidth - 25;
    const valueX = summaryX + labelWidth;

    setFontStyle(doc, 12, 'normal', COLORS.mediumGray);

    // Subtotal
    doc.text('Subtotal', summaryX, yPosition);
    doc.text(`$${(summary.subtotal || 0).toFixed(2)}`, valueX, yPosition, { align: 'right' });
    yPosition += 7;

    // Additional Charges
    doc.text('Additional Charges', summaryX, yPosition);
    doc.text(
        `$${(summary.totalAdditionalCharges || 0).toFixed(2)}`,
        valueX,
        yPosition,
        { align: 'right' }
    );
    yPosition += 7;

    // Tax (if present in summary)
    if (summary.tax !== undefined && summary.tax !== null) {
        doc.text('Tax (0%)', summaryX, yPosition);
        doc.text(`$${(summary.tax || 0).toFixed(2)}`, valueX, yPosition, { align: 'right' });
        yPosition += 7;
    }

    // Payment method (if present in summary)
    if (summary.paymentMethod) {
        doc.text(`Total Amount - ${summary.paymentMethod}`, summaryX, yPosition);
        doc.text(`$${(summary.totalDue || 0).toFixed(2)}`, valueX, yPosition, { align: 'right' });
        yPosition += 7;
    } else {
        // Default: Total Amount Due (with dark blue background)
        doc.setFillColor(COLORS.darkBlue.r, COLORS.darkBlue.g, COLORS.darkBlue.b);
        doc.rect(summaryX - 5, yPosition - 3, labelWidth + 50, 6, 'F');

        setFontStyle(doc, 12, 'bold', { r: 255, g: 255, b: 255 }); // White text
        doc.text('Total Amount Due', summaryX, yPosition);
        doc.text(`$${(summary.totalDue || 0).toFixed(2)}`, valueX, yPosition, {
            align: 'right',
        });
        yPosition += 7;
    }

    return yPosition;
};

/**
 * Helper: Add footer section
 */
const addFooter = (doc, pageHeight) => {
    const footerY = pageHeight - TABLE_CONFIG.footerOffset;
    const footerLines = [
        'This invoice was automatically generated by 3PLVision.',
        'For billing queries contact info@3plvision.com',
        '© 2026 3PLVision. All rights reserved.',
    ];

    setFontStyle(doc, 10, 'normal', COLORS.lightText);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page++) {
        doc.setPage(page);
        let yFooter = footerY;

        footerLines.forEach(line => {
            doc.text(line, PDF_CONFIG.margin.left + PDF_CONFIG.pageWidth / 2, yFooter, { align: 'center' });
            yFooter += 4;
        });
    }
};

/**
 * Utility: Set font style
 */
const setFontStyle = (doc, size, weight, color) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', weight === 'bold' ? 'bold' : 'normal');
    doc.setTextColor(color.r, color.g, color.b);
};

/**
 * Utility: Format date
 */
const formatDate = dateStr => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
        console.warn(`Failed to parse date "${dateStr}":`, error);
        return dateStr;
    }
};
